import { type ElementType, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { cardBase } from "@/lib/card-styles";

interface SectionPanelProps {
  icon?: ElementType;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  /** Fixed left-rail accent color — same `border-l-4` + inline `borderLeftColor` technique as
   *  `AccentRailDisclosure`'s `accentColor`. Omit for the plain, uncolored border every other
   *  `SectionPanel` uses. */
  accentColor?: string;
}

export function SectionPanel({
  icon: Icon,
  title,
  subtitle,
  actions,
  children,
  className,
  contentClassName,
  accentColor,
}: SectionPanelProps) {
  return (
    <div
      className={cn(
        cardBase,
        "overflow-hidden",
        accentColor && "border-l-4",
        className,
      )}
      style={accentColor ? { borderLeftColor: accentColor } : undefined}
    >
      <header className="px-4 py-3 border-b border-border flex items-center gap-2 shrink-0">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">{title}</div>
          {subtitle && (
            <div className="text-[11px] text-muted-foreground">{subtitle}</div>
          )}
        </div>
        {actions}
      </header>
      <div className={cn("p-4", contentClassName)}>{children}</div>
    </div>
  );
}
